import type {PriceTierItem, PriceTierType, PriceTierVersion} from "@/types/trustpilotPricing";

export function isReputechTrustpilotPriceTier(
  priceTier: Partial<PriceTierType> | null | undefined,
): priceTier is PriceTierType {
  return (
    (priceTier?.type === "trust-pilot" || priceTier?.type === "trustpilot") &&
    priceTier.frontend_domain?.trim().toLowerCase().replace(/^www\./, "") ===
      "reputech.io" &&
    Array.isArray(priceTier.tiers) &&
    priceTier.tiers.length > 0
  );
}

/**
 * Get the price tier item based on review count
 * @param reviewCount - Number of reviews
 * @param priceTier - Price tier configuration
 * @returns The matching price tier item or null
 */
export function getPriceTierByReviewCount(
  reviewCount: number,
  priceTier?: PriceTierType,
): PriceTierItem | null {
  if (!priceTier || !priceTier.tiers || priceTier.tiers.length === 0) {
    return null;
  }

  if (Number.isNaN(reviewCount)) {
    return null;
  }

  // Preserve first-match behavior for overlapping boundaries from the API.
  for (const tier of priceTier.tiers) {
    const inRange =
      reviewCount >= tier.min_reviews &&
      (tier.max_reviews === null || reviewCount <= tier.max_reviews);

    if (inRange) {
      return tier;
    }
  }

  // No matching tier found. This can happen when the review count falls
  // in a gap between tiers, or when no open-ended tier catches high counts.
  return null;
}

/**
 * Get the next price tier based on current review count
 * @param reviewCount - Current review count
 * @param priceTier - Price tier configuration
 * @returns The next price tier item or null if at max tier
 */
export function getNextPriceTierItem(
  reviewCount: number,
  priceTier?: PriceTierType,
): PriceTierItem | null {
  if (!priceTier || !priceTier.tiers || priceTier.tiers.length === 0) {
    return null;
  }

  const currentTier = getPriceTierByReviewCount(reviewCount, priceTier);
  if (!currentTier) {
    return null;
  }

  // Find the index of current tier
  const currentIndex = priceTier.tiers.findIndex(
    (tier) =>
      tier.min_reviews === currentTier.min_reviews &&
      tier.max_reviews === currentTier.max_reviews &&
      tier.price === currentTier.price,
  );

  // Return next tier if exists
  if (currentIndex >= 0 && currentIndex < priceTier.tiers.length - 1) {
    return priceTier.tiers[currentIndex + 1];
  }

  return null;
}

/**
 * Get the tier limit (max_reviews) for current tier
 * Returns -1 if unlimited (max_reviews is null)
 */
export function getTierLimit(
  reviewCount: number,
  priceTier?: PriceTierType,
): number {
  const currentTier = getPriceTierByReviewCount(reviewCount, priceTier);
  if (!currentTier) {
    return -1;
  }

  return currentTier.max_reviews ?? -1;
}

/**
 * Get reviews needed to reach next tier
 */
export function getReviewsToNextTier(
  reviewCount: number,
  priceTier?: PriceTierType,
): number | null {
  const nextTier = getNextPriceTierItem(reviewCount, priceTier);
  if (!nextTier) {
    return null; // Already at max tier
  }

  return Math.max(0, nextTier.min_reviews - reviewCount);
}

/**
 * Calculate potential savings by reaching the next tier
 * @param selectedReviewsCount - Current number of selected reviews
 * @param priceTier - Price tier configuration
 * @returns Calculated savings or null if at max tier
 */
export function getSavings(
  selectedReviewsCount: number,
  priceTier?: PriceTierType,
): number | null {
  const currentTier = getPriceTierByReviewCount(
    selectedReviewsCount,
    priceTier,
  );
  const nextTier = getNextPriceTierItem(selectedReviewsCount, priceTier);

  if (!currentTier || !nextTier) {
    return null; // At max tier or no tiers available
  }

  // No savings if next tier has no max (unlimited tier)
  if (currentTier.max_reviews === null) {
    return null;
  }

  // No savings if already at/above the limit
  if (selectedReviewsCount >= currentTier.max_reviews) {
    return 0;
  }

  // Misconfigured tiers (next tier not cheaper)
  if (nextTier.price >= currentTier.price) {
    return 0;
  }

  const reviewsToNextTier = nextTier.min_reviews - selectedReviewsCount;
  const savings = (currentTier.price - nextTier.price) * reviewsToNextTier;

  // Clamp and round down to avoid overstating
  const clamped = Math.max(0, savings);
  return Number.parseFloat((Math.floor(clamped * 100) / 100).toFixed(2));
}

/**
 * Get the price per unit based on review count
 * @param reviewCount - Number of reviews
 * @param priceTier - Price tier configuration
 * @returns Price per unit
 */
export function getPricePerUnit(
  reviewCount: number,
  priceTier?: PriceTierType,
): number {
  const tier = getPriceTierByReviewCount(reviewCount, priceTier);
  return tier?.price ?? 0.0;
}

/**
 * Calculate the total before coupons for the selected pricing model.
 * Volume pricing applies the tier reached by the full order to every review.
 * Graduated pricing charges each review at the tier containing its position.
 */
export function calculatePriceTierTotal(
  reviewCount: number,
  priceTier?: PriceTierType,
  version: PriceTierVersion = priceTier?.version ?? "volume_pricing",
): number {
  if (!Number.isFinite(reviewCount) || reviewCount <= 0) {
    return 0;
  }

  const normalizedReviewCount = Math.floor(reviewCount);

  if (version !== "graduated_pricing") {
    return (
      getPricePerUnit(normalizedReviewCount, priceTier) * normalizedReviewCount
    );
  }

  let total = 0;

  for (
    let reviewPosition = 1;
    reviewPosition <= normalizedReviewCount;
    reviewPosition += 1
  ) {
    total += getPriceTierByReviewCount(reviewPosition, priceTier)?.price ?? 0;
  }

  return total;
}
